import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { StepComponent } from '@app/components';
import { AnalyticEvents } from '@app/enums';
import { Employee } from '@app/models/employee/employee.model';
import { TimeOffPolicy } from '@app/models/time-off-v3/time-off-policy.model';
import { TimeOffTransaction } from '@app/models/time-off-v3/time-off-transaction.model';
import { EmployeeAdditionForm, EmployeeRemovalForm } from '@app/modules/time-off-v3/forms';
import { TimeOffService } from '@app/modules/time-off-v3/services';
import { AnalyticService, NotifyService } from '@app/services';

@Component({
    templateUrl: './assign-policy.template.html',
    styleUrls: ['./assign-policy.style.scss'],
})
export class AssignPolicyView implements OnInit {
    @ViewChild('employeeAdditionForm') employeeAdditionForm: EmployeeAdditionForm;
    @ViewChild('employeeRemovalForm') employeeRemovalForm: EmployeeRemovalForm;

    timeOffPolicy: TimeOffPolicy;
    timeOffBalanceTransactions: TimeOffTransaction[] = [];
    additions: Employee[] = [];
    removals: Employee[] = [];
    assignedEmployees: Employee[] = [];
    policyId: number = this.route.snapshot.params.policyId;
    isLoading = true;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private notify: NotifyService,
        private timeOffService: TimeOffService,
        private analyticService: AnalyticService
    ) {}

    async ngOnInit(): Promise<void> {
        this.loadPolicy();
    }

    updateAdditionsAndRemovals(additionsAndRemovals: { additions: Employee[]; removals: Employee[] }): void {
        this.additions = additionsAndRemovals.additions;
        this.removals = additionsAndRemovals.removals;
    }

    async onNextStep([currentStep]: [StepComponent, number]): Promise<void> {
        // Based on the current step
        switch (currentStep.stepKey) {
            case 'Removals':
                this.employeeAdditionForm.setEffectiveAtDate();

                // Get the transactions for processing later.
                this.timeOffBalanceTransactions = this.employeeAdditionForm.timeOffBalanceTransactions;
                break;
        }
    }

    shouldMoveToNextStep = (): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
            // Only move to the next step if there are any new additions or removals
            if (this.additions.length > 0 || this.removals.length > 0) {
                resolve();
                return;
            }
            this.notify.warn('time-off-v3.pleaseAssignOrRemoveNewEmployees');
            reject();
        });
    };

    loadPolicy(): void {
        this.isLoading = true;

        TimeOffPolicy.with(['currentlyAssignedNonTerminatedEmployees', 'futureAssignedNonTerminatedEmployees'])
            .find(this.policyId)
            .then((timeOffPolicy: TimeOffPolicy) => {
                this.timeOffPolicy = timeOffPolicy;
                this.assignedEmployees = this.combineCurrentAndFutureAssignedEmployees(
                    timeOffPolicy.currentlyAssignedNonTerminatedEmployees,
                    timeOffPolicy.futureAssignedNonTerminatedEmployees
                );
                this.isLoading = false;
            })
            .catch((err) => {
                this.notify.error(err);
                this.isLoading = false;
            });
    }

    combineCurrentAndFutureAssignedEmployees(
        currentlyAssignedNonTerminatedEmployees: Employee[],
        futureAssignedNonTerminatedEmployees: Employee[]
    ): Employee[] {
        // Go over each to mark these as scheduled employees
        futureAssignedNonTerminatedEmployees.forEach((employee: Employee) => {
            employee.scheduledForFuturePolicyAssignment = true;
        });
        // Combine them into one array
        return [...currentlyAssignedNonTerminatedEmployees, ...futureAssignedNonTerminatedEmployees];
    }

    async onSave(): Promise<void> {
        this.isLoading = true;

        // Set the detach array for the employees which will be removed from this policy.
        this.employeeRemovalForm.setEffectiveToDate();

        await Promise.all(
            this.timeOffBalanceTransactions.map(async (transaction: TimeOffTransaction) => {
                return await transaction.save();
            })
        );

        try {
            await this.timeOffService.assignAndUnassignEmployeesToPolicy(
                this.timeOffPolicy.id,
                { employees: this.employeeAdditionForm.policyAttach },
                { employees: this.employeeRemovalForm.policyDetach }
            );
        } catch (e) {
            this.notify.error(e.error.errors[0].detail);
            return;
        } finally {
            this.isLoading = false;
        }

        this.analyticService.trackEvent(AnalyticEvents.TimeOffTypesAndPoliciesAssignPolicy);
        this.notify.success('time-off-v3.employeesSuccessfullyAssignedToPolicy');
        this.redirect();
    }

    redirect(): void {
        this.router.navigate(['/time-off/v3/types-and-policies'], { relativeTo: this.route });
    }
}
